<script lang="ts">
  import { BRAND_NAME } from '../lib/brand';
  import { onMount, onDestroy } from 'svelte';
  import { api } from '../lib/api';

  /**
   * Custom audio UI on top of a hidden Bunny iframe. The iframe handles the
   * actual (DRM-encrypted) playback; we control it via player.js postMessage
   * and draw our own waveform-style controls.
   *
   * Interaction model on the track:
   *   - Single tap (no drag) → play/pause toggle
   *   - Drag → relative seek (current playhead shifts by drag delta, NOT
   *     absolute seek to the tap point). Throttled to ~10Hz.
   */
  interface PlayerJsPlayer {
    on: (
      event: 'ready' | 'play' | 'pause' | 'ended' | 'timeupdate',
      cb: (data?: { seconds: number; duration: number }) => void,
    ) => void;
    play: () => void;
    pause: () => void;
    setCurrentTime: (seconds: number) => void;
  }
  interface PlayerJsCtor {
    Player: new (iframe: HTMLIFrameElement) => PlayerJsPlayer;
  }
  function getPlayerJs(): PlayerJsCtor | undefined {
    return (window as unknown as { playerjs?: PlayerJsCtor }).playerjs;
  }

  interface Chapter {
    title: string;
    start: number;
    end?: number;
  }
  interface Props {
    src: string;
    /** Optional — omit for previews where the material isn't saved yet. */
    materialId?: string;
    /** When false, skip the postProgress calls (admin preview mode). */
    trackProgress?: boolean;
    /** Mirrored from Bunny on sync; renders markers + clickable list. */
    chapters?: Chapter[];
    /** Material title — shown on lock screen via Media Session API. */
    title?: string;
    /** Subtitle line on lock screen (usually category name). */
    artist?: string;
  }
  let {
    src,
    materialId,
    trackProgress = true,
    chapters = [],
    title = '',
    artist = '',
  }: Props = $props();
  const canTrack = $derived(trackProgress && !!materialId);

  let iframeEl: HTMLIFrameElement | undefined = $state(undefined);
  let trackAreaEl: HTMLDivElement | undefined = $state(undefined);
  let player: PlayerJsPlayer | null = null;
  let ready = $state(false);
  let playing = $state(false);
  let currentTime = $state(0);
  let duration = $state(0);

  // Drag state for the relative-seek pointer flow.
  let dragStartX = 0;
  let dragStartTime = 0;
  let dragMoved = false;
  let activePointerId: number | null = null;
  const TAP_THRESHOLD_PX = 5;
  const SEEK_THROTTLE_MS = 100;
  let lastSeekAt = 0;

  const PROGRESS_THROTTLE_SEC = 5;
  const RESUME_THRESHOLD_SEC = 5;
  const RESUME_CUTOFF_PCT = 95;
  let lastSent = -Infinity;

  // Decorative pseudo-waveform — Bunny's DRM iframe doesn't expose raw audio
  // so we draw a stylized constant pattern, seeded from `src` so each track
  // has a unique shape that stays consistent across mounts.
  const BAR_COUNT = 40;
  function hashStr(s: string): number {
    let h = 2166136261 >>> 0;
    for (let i = 0; i < s.length; i++) {
      h ^= s.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return h >>> 0;
  }
  const bars = $derived.by(() => {
    const seed = hashStr(src);
    const out: number[] = [];
    for (let i = 0; i < BAR_COUNT; i++) {
      const a = Math.sin(i * 0.7 + (seed % 1000) * 0.01);
      const b = Math.sin(i * 1.7 + (seed % 7919) * 0.003);
      const v = (a * 0.5 + b * 0.3 + 1) / 2; // 0..1
      out.push(Math.round(28 + v * 64));      // 28..92 (%)
    }
    return out;
  });

  $effect(() => {
    if (!iframeEl) return;
    const PlayerJs = getPlayerJs();
    if (!PlayerJs) {
      console.warn('player.js not loaded — AudioPlayer falls back to silent state');
      return;
    }
    const p = new PlayerJs.Player(iframeEl);
    player = p;

    p.on('ready', () => {
      ready = true;
      if (canTrack) {
        api
          .playbackState(materialId!)
          .then((state) => {
            if (
              state.lastPosition > RESUME_THRESHOLD_SEC &&
              state.maxProgressPct < RESUME_CUTOFF_PCT
            ) {
              p.setCurrentTime(state.lastPosition);
              currentTime = state.lastPosition;
            }
          })
          .catch(() => {
            /* fresh start — fine */
          });
      }
    });

    p.on('play', () => { playing = true; });
    p.on('pause', () => { playing = false; });
    p.on('ended', () => {
      playing = false;
      currentTime = duration;
      if (canTrack && duration > 0) {
        api.postProgress(materialId!, { sec: duration, duration }).catch(() => {});
      }
    });

    p.on('timeupdate', (data) => {
      if (!data || !data.duration) return;
      duration = data.duration;
      // While dragging, currentTime is driven locally; ignore upstream ticks
      // to avoid the playhead jittering on top of the drag.
      if (activePointerId === null) currentTime = data.seconds;
      if (canTrack && Math.abs(data.seconds - lastSent) >= PROGRESS_THROTTLE_SEC) {
        lastSent = data.seconds;
        api
          .postProgress(materialId!, { sec: data.seconds, duration: data.duration })
          .catch(() => {});
      }
    });

    return () => {
      player = null;
    };
  });

  onMount(() => {
    if (!getPlayerJs()) {
      /* player.js comes from CDN in index.html; trust the load order. */
    }
  });

  onDestroy(() => {
    player = null;
  });

  function toggle(): void {
    if (!player || !ready) return;
    if (playing) player.pause();
    else player.play();
  }

  // === Pointer-driven scrubbing on the track ===
  function onTrackPointerDown(e: PointerEvent): void {
    if (!ready || !trackAreaEl) return;
    // Capture so move/up still hit us even if the finger drifts off-element.
    trackAreaEl.setPointerCapture(e.pointerId);
    activePointerId = e.pointerId;
    dragStartX = e.clientX;
    dragStartTime = currentTime;
    dragMoved = false;
  }

  function onTrackPointerMove(e: PointerEvent): void {
    if (activePointerId === null || e.pointerId !== activePointerId || !trackAreaEl) return;
    const dx = e.clientX - dragStartX;
    if (!dragMoved && Math.abs(dx) < TAP_THRESHOLD_PX) return;
    dragMoved = true;
    if (duration <= 0) return;
    const w = trackAreaEl.clientWidth;
    const deltaTime = (dx / w) * duration;
    const t = Math.max(0, Math.min(duration, dragStartTime + deltaTime));
    currentTime = t;
    const now = performance.now();
    if (player && now - lastSeekAt >= SEEK_THROTTLE_MS) {
      lastSeekAt = now;
      player.setCurrentTime(t);
    }
  }

  function onTrackPointerUp(e: PointerEvent): void {
    if (activePointerId === null || e.pointerId !== activePointerId) return;
    if (trackAreaEl?.hasPointerCapture(e.pointerId)) {
      trackAreaEl.releasePointerCapture(e.pointerId);
    }
    activePointerId = null;
    if (dragMoved) {
      // Finalize seek to the visual position (in case throttle skipped the
      // last move tick).
      if (player) player.setCurrentTime(currentTime);
    } else {
      // No drag → treat as tap on track = play/pause toggle.
      toggle();
    }
    dragMoved = false;
  }

  function fmt(s: number): string {
    if (!Number.isFinite(s) || s < 0) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  }

  const pct = $derived(duration > 0 ? (currentTime / duration) * 100 : 0);

  // === Media Session API ===
  // Lets OS show track metadata + play/pause/seek controls on lock screen
  // and headphones, while the app is still active (Telegram open). Doesn't
  // give true background playback inside a Mini App — that needs host-app
  // support — but it covers the «екран заблоковано, телефон у кишені»
  // case cleanly. Feature-detected so legacy WebViews degrade silently.
  function hasMediaSession(): boolean {
    return typeof navigator !== 'undefined' && 'mediaSession' in navigator;
  }

  $effect(() => {
    if (!hasMediaSession()) return;
    navigator.mediaSession.metadata = new MediaMetadata({
      title: title || BRAND_NAME,
      artist: artist || BRAND_NAME,
    });
    return () => {
      if (hasMediaSession()) navigator.mediaSession.metadata = null;
    };
  });

  // Register Media Session action handlers eagerly — iOS needs them in place
  // BEFORE the first user gesture, otherwise the screen-lock pauses the
  // playback on its first run (subsequent plays work because the session is
  // active by then). Handlers close over the latest `player` ref via the
  // variable lookup, so a null player at register time isn't a problem.
  $effect(() => {
    if (!hasMediaSession()) return;
    const ms = navigator.mediaSession;
    ms.setActionHandler('play', () => player?.play());
    ms.setActionHandler('pause', () => player?.pause());
    ms.setActionHandler('seekto', (e) => {
      if (!player || typeof e.seekTime !== 'number') return;
      const t = Math.max(0, Math.min(duration, e.seekTime));
      player.setCurrentTime(t);
      currentTime = t;
    });
    ms.setActionHandler('seekbackward', (e) => {
      if (!player) return;
      const skip = e.seekOffset ?? 10;
      const t = Math.max(0, currentTime - skip);
      player.setCurrentTime(t);
      currentTime = t;
    });
    ms.setActionHandler('seekforward', (e) => {
      if (!player) return;
      const skip = e.seekOffset ?? 10;
      const t = Math.min(duration, currentTime + skip);
      player.setCurrentTime(t);
      currentTime = t;
    });
    return () => {
      if (!hasMediaSession()) return;
      const m = navigator.mediaSession;
      m.setActionHandler('play', null);
      m.setActionHandler('pause', null);
      m.setActionHandler('seekto', null);
      m.setActionHandler('seekbackward', null);
      m.setActionHandler('seekforward', null);
    };
  });

  $effect(() => {
    if (!hasMediaSession()) return;
    navigator.mediaSession.playbackState = playing ? 'playing' : 'paused';
  });

  $effect(() => {
    if (!hasMediaSession() || !duration) return;
    // Some WebViews don't implement setPositionState; guard via try/catch so
    // a misformed call doesn't bubble up.
    try {
      navigator.mediaSession.setPositionState?.({
        duration,
        position: Math.min(currentTime, duration),
        playbackRate: 1,
      });
    } catch {
      /* unsupported */
    }
  });

  /** Sorted chapter list with derived end time so highlighting + percent
   *  positions are stable. End falls back to next chapter's start (or
   *  duration for the last one). */
  const sortedChapters = $derived.by(() => {
    if (!chapters || chapters.length === 0) return [];
    const sorted = [...chapters].sort((a, b) => a.start - b.start);
    return sorted.map((c, i) => ({
      title: c.title,
      start: c.start,
      end: c.end ?? sorted[i + 1]?.start ?? duration ?? Number.POSITIVE_INFINITY,
    }));
  });

  const activeChapterIdx = $derived.by(() => {
    for (let i = sortedChapters.length - 1; i >= 0; i--) {
      if (currentTime >= sortedChapters[i].start) return i;
    }
    return -1;
  });

  function jumpToChapter(start: number): void {
    if (!player || duration <= 0) return;
    const t = Math.max(0, Math.min(duration, start));
    player.setCurrentTime(t);
    currentTime = t;
    if (!playing) player.play();
  }
</script>

<div class="audio-block">
<div class="audio-player">
  <iframe
    bind:this={iframeEl}
    {src}
    title="audio playback"
    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
    aria-hidden="true"
  ></iframe>

  <button
    type="button"
    class="play-btn"
    onclick={toggle}
    disabled={!ready}
    aria-label={playing ? 'Пауза' : 'Грати'}
  >
    {#if playing}
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <rect x="6" y="5" width="4" height="14" rx="1"/>
        <rect x="14" y="5" width="4" height="14" rx="1"/>
      </svg>
    {:else}
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M8 5l11 7-11 7V5z"/>
      </svg>
    {/if}
  </button>

  <div class="track">
    <!-- svelte-ignore a11y_no_static_element_interactions a11y_no_noninteractive_element_interactions -->
    <div
      class="wave-row"
      bind:this={trackAreaEl}
      role="slider"
      tabindex="0"
      aria-label="Позиція"
      aria-valuemin="0"
      aria-valuemax="100"
      aria-valuenow={Math.round(pct)}
      onpointerdown={onTrackPointerDown}
      onpointermove={onTrackPointerMove}
      onpointerup={onTrackPointerUp}
      onpointercancel={onTrackPointerUp}
    >
      <div class="bars" aria-hidden="true">
        {#each bars as h, i (i)}
          <div
            class="bar"
            class:played={pct >= ((i + 0.5) / BAR_COUNT) * 100}
            style="height: {h}%"
          ></div>
        {/each}
      </div>
      {#if duration > 0}
        {#each sortedChapters as ch (ch.start)}
          {@const markerPct = (ch.start / duration) * 100}
          {#if markerPct > 0 && markerPct < 100}
            <div
              class="chapter-marker"
              style="left: {markerPct}%"
              aria-hidden="true"
              title={`${fmt(ch.start)} · ${ch.title}`}
            ></div>
          {/if}
        {/each}
      {/if}
    </div>
    <div class="time">
      <span>{fmt(currentTime)}</span>
      <span>{fmt(duration)}</span>
    </div>
  </div>
</div>

{#if sortedChapters.length > 0}
  <ul class="chapter-list">
    {#each sortedChapters as ch, i (ch.start)}
      <li>
        <button
          type="button"
          class="chapter-item"
          class:active={i === activeChapterIdx}
          onclick={() => jumpToChapter(ch.start)}
        >
          <span class="chapter-time">{fmt(ch.start)}</span>
          <span class="chapter-title">{ch.title}</span>
        </button>
      </li>
    {/each}
  </ul>
{/if}
</div>

<style>
  .audio-block { margin-bottom: 18px; }
  .audio-player {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px 16px;
    background: linear-gradient(135deg, var(--bg2), var(--bg));
    border: 1px solid var(--border);
    border-radius: 14px;
    position: relative;
  }
  .audio-block .chapter-list { margin-top: 10px; }
  iframe {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
    pointer-events: none;
    border: 0;
    left: 0;
    top: 0;
  }
  .play-btn {
    width: 44px;
    height: 44px;
    flex-shrink: 0;
    border-radius: 50%;
    border: none;
    background: linear-gradient(135deg, var(--pu), var(--pu3));
    color: var(--text-light);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(168, 80, 47, 0.3);
    transition: transform 0.15s var(--ease);
  }
  .play-btn:active:not(:disabled) { transform: scale(0.95); }
  .play-btn:disabled { opacity: 0.4; cursor: not-allowed; box-shadow: none; }

  .track {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  /* Bars + pointer-capturing wrapper. No thumb on the track per UX request —
     drag is relative and tap toggles playback. */
  .wave-row {
    position: relative;
    height: 32px;
    cursor: pointer;
    touch-action: none;            /* let our pointer handlers own the drag */
    user-select: none;
    -webkit-user-select: none;
    outline: none;
  }
  .bars {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 2px;
    pointer-events: none;          /* clicks belong to .wave-row */
  }
  .bar {
    flex: 1;
    background: var(--border2);
    border-radius: 1.5px;
    min-height: 4px;
    transition: background 0.08s linear;
  }
  .bar.played { background: var(--pu); }
  .chapter-marker {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 2px;
    margin-left: -1px;
    background: var(--text);
    opacity: 0.55;
    border-radius: 1px;
    pointer-events: none;
  }

  .chapter-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .chapter-item {
    width: 100%;
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 8px 12px;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 10px;
    color: var(--text);
    font-family: inherit;
    font-size: 13px;
    line-height: 1.4;
    text-align: left;
    cursor: pointer;
  }
  .chapter-item:hover {
    background: var(--bg2);
  }
  .chapter-item.active {
    background: var(--pu-light);
    color: var(--accent-text);
    font-weight: 600;
  }
  .chapter-time {
    flex-shrink: 0;
    font-variant-numeric: tabular-nums;
    font-size: 11px;
    color: var(--text-muted);
    min-width: 36px;
    /* Pad the timestamp so it aligns visually with the first line of a
       wrapping title (font-size 13 × line-height 1.4 ≈ 18.2px). */
    line-height: 1.6;
  }
  .chapter-item.active .chapter-time { color: var(--accent-text); }
  .chapter-title {
    flex: 1;
    min-width: 0;
    /* Long titles wrap onto multiple lines instead of getting cut off. */
    white-space: normal;
    overflow-wrap: anywhere;
  }

  .time {
    display: flex;
    justify-content: space-between;
    font-size: 11px;
    color: var(--text-muted);
    font-variant-numeric: tabular-nums;
  }
</style>
